import { t } from '@/locales'

export type TValidationErrors =
  | 'form/required'
  | 'form/email-invalid'
  | 'form/phone-required'
  | 'form/phone-invalid'
  | 'form/password-min-length'
  | 'form/password-upper-and-lower-case'
  | 'form/password-number'
  | 'form/password-match'
  | 'form/password-no-spaces'
  | 'form/no-malicious-code'
  | 'form/number-min'
  | 'form/number-max'
  | 'form/number-decimals'
  | `form/string-min-length:${number}`
  | `form/string-max-length:${number}`

const errorMessages = {
  // Firebase Auth errors
  'auth/user-not-found': () => t('error.auth.user-not-found'),
  'auth/invalid-credential': () => t('error.auth.invalid-credentials'),
  'auth/email-already-exists': () => t('error.auth.email-already-exists'),
  'auth/not-authorized': () => t('error.auth.not-authorized'),

  // Form validation errors
  'form/required': () => t('error.form.required'),
  'form/email-invalid': () => t('error.form.email-invalid'),
  'form/phone-required': () => t('error.form.phone-required'),
  'form/phone-invalid': () => t('error.form.phone-invalid'),
  'form/password-min-length': () =>
    t('error.form.password-min-length', { min: 8 }),
  'form/password-upper-and-lower-case': () =>
    t('error.form.password-upper-and-lower-case'),
  'form/password-number': () => t('error.form.password-number'),
  'form/password-no-spaces': () => t('error.form.password-no-spaces'),
  'form/password-match': () => t('error.form.password-match'),
  'form/no-malicious-code': () => t('error.form.no-malicious-code'),
  'form/number-min': () => t('error.form.number-min'),
  'form/number-max': () => t('error.form.number-max'),
  'form/number-decimals': () => t('error.form.number-decimals'),
  'form/required-array-selection': () =>
    t('error.form.required-array-selection'),
  'form/hex-color-no-hash': () => t('error.form.hex-color-no-hash'),
  'form/hex-color-invalid': () => t('error.form.hex-color-invalid'),
  'form/vat-number-invalid': () => t('error.form.vat-number-invalid'),
}

type ErrorCode = keyof typeof errorMessages

export const getErrorMessage = (errorCode: ErrorCode | string) => {
  if (errorCode.startsWith('form/string-min-length:')) {
    return t('error.form.string-min-length', { min: errorCode.split(':')[1] })
  }
  if (errorCode.startsWith('form/string-max-length:')) {
    return t('error.form.string-max-length', { max: errorCode.split(':')[1] })
  }

  let text = errorMessages[errorCode as ErrorCode]?.()

  if (!text) {
    text = t('error.common.standard')
  }

  return text
}


/**
 * Creating a customer user whose email already belongs to another user — of any type: a business
 * or insurance customer user, or lab staff (Firebase Auth allows one account per email). lab-cloud
 * answers `already-exists` (MIL-402). Says "another user" on purpose: the clash is often with a
 * user the form's own list does not show.
 */
export const getEmailInUseByAnotherUserMessage = (): string =>
  t('W: E-mailen er allerede i brug af en anden bruger')
